import TosPdfViewerModal from '@/components/modals/TosPdfViewerModal';
import { SessionContext } from '@/context/SessionContext';
import ConductionAPI from '@/javascript/conduction-api';
import { Utils } from '@/javascript/utils';
import { Button, Card, Checkbox, Flex, Group, Loader, MultiSelect, PasswordInput, Stack, Text, TextInput, Title } from '@mantine/core';
import { useForm } from '@mantine/form';
import { IconAlertCircle, IconAt, IconLock, IconMusic, IconUser } from '@tabler/icons-react';
import { useContext, useEffect, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';

const InstrumentalistRegistrationModal: React.FC = () => {
	const session = useContext(SessionContext);
	if (session === undefined) {
		throw new Error("Modals must be a child of sessionContext.");
	}

	const navigate = useNavigate();
	const [isSubmitting, setIsSubmitting] = useState(false);
	const [accessCode, setAccessCode] = useState<string | null | undefined>(undefined);
	const [instruments, setInstruments] = useState<{ id: string; name: string }[] | null>(null);
	const [searchParams] = useSearchParams();
	const [tosViewerOpen, setTosViewerOpen] = useState(false);

	useEffect(() => {
		const foundAccessCode = searchParams.get('accessCode');
		setAccessCode(foundAccessCode || null);
	}, [searchParams]);

	useEffect(() => {
		setInstruments(session.instruments);
	}, [session.instruments]);

	const form = useForm({
		initialValues: {
			username: '',
			email: '',
			firstName: '',
			lastName: '',
			password: '',
			confirmPassword: '',
			instruments: [] as string[],
			tosAgreed: false
		},
		validate: {
			username: (value: string) => (value.trim().length === 0 ? 'Username is required' : null),
			email: (value: string) => !/^\S+@\S+\.\S+$/.test(value) ? 'Invalid email address' : null,
			firstName: (value: string) => (value.trim().length === 0 ? 'First name is required' : null),
			lastName: (value: string) => (value.trim().length === 0 ? 'Last name is required' : null),
			password: (value: string) => {
				if (value.length < 6 || value.length > 30) return 'Password must be between 6 and 30 characters';
				if (!/[a-z]/.test(value)) return 'Password must contain a lowercase letter';
				if (!/[A-Z]/.test(value)) return 'Password must contain an uppercase letter';
				if (!/[0-9]/.test(value)) return 'Password must contain a number';
				if (!/[^a-z\d]/i.test(value)) return 'Password must contain a special character';
				return null;
			},
			confirmPassword: (value: any, values: { password: any; }) =>
				value !== values.password ? 'Passwords do not match' : null,
			instruments: (value: string[]) => (value.length === 0 ? 'At least one instrument is required' : null),
			tosAgreed: (value: boolean) => (!value ? 'You must agree to the Terms of Service to register an account.' : null),
		},
	});

	const handleRegister = async () => {
		if (!accessCode) {
			Utils.displayNotification("Access Code Missing", "Contact support for help creating an instrumentalist account.", "red", <IconAlertCircle size={16} />, true, false);
			return;
		}

		const notificationId = Utils.displayNotification("Creating Account", "This may take a few moments...", "blue", "loading");

		setIsSubmitting(true);
		try {
			let registrationResult;

			if (accessCode !== null) {
				registrationResult = await ConductionAPI.registerInstrumentalist(
					form.values.username,
					form.values.email,
					form.values.firstName,
					form.values.lastName,
					form.values.password,
					form.values.confirmPassword,
					form.values.instruments,
					accessCode
				);
			} else {
				Utils.updateNotification(notificationId, "Registration Failed", "Contact support for help creating an instrumentalist account.", "red", <IconAlertCircle size={16} />, true, false);
				return;
			}

			if (registrationResult) {
				Utils.updateNotification(notificationId, "Account Created", null, "green", <IconUser size={16} />);

				// Log the user in automatically
				const automaticallyAgreeTOS = session.currentTos != null && form.values.tosAgreed;
				if (await session.login(form.values.username, form.values.password, false, automaticallyAgreeTOS)) {
					navigate("/instrumentalist")
				}
			} else {
				Utils.updateNotification(notificationId, "Username or Email Taken", "The entered username or email may already be in use. Try a different one.", "red", <IconAlertCircle size={16} />, true, 10000);
			}
		} catch (error) {
			Utils.updateNotification(notificationId, "Registration Error", "An error occurred during account creation. Please try again later.", "red", <IconAlertCircle size={16} />, true, false);
		} finally {
			setIsSubmitting(false);
		}
	};

	useEffect(() => {
		if (session.currentTos == null) {
			session.fetchCurrentTos();
		}
	}, [session.user?.agreedToCurrentTos, session.currentTos]);

	return (
		<Flex
			w="100%"
			h="100%"
			justify="center"
			align="center"
		>
			<Card
				w="60%"
				miw="500px"
			>
				<Stack>
					<Title order={2} mb={20}>Create an Conduction Instrumentalist Account</Title>

					{accessCode === undefined || instruments === null ? (
						<Stack>
							<Loader />
						</Stack>
					) : accessCode === null ? (
						<Stack>
							<Text size="lg">
								An access code is required to create an instrumentalist account. Please contact support for assistance.
							</Text>
						</Stack>
					) : (
						<form onSubmit={form.onSubmit(handleRegister)}>
							<Stack>
								<TextInput
									label={
										<Group>
											<IconUser size={20} />
											<Text>Username</Text>
											<Text c="dimmed" fz="sm">(must be unique, used for signing in)</Text>
										</Group>
									}
									{...form.getInputProps('username')}
									required
									withAsterisk={false}
									disabled={isSubmitting}
								/>

								<TextInput
									label={
										<Group>
											<IconAt size={20} />
											<Text>Email</Text>
											<Text c="dimmed" fz="sm">(for notifications when you are assigned music to record)</Text>
										</Group>
									}
									{...form.getInputProps('email')}
									required
									withAsterisk={false}
									disabled={isSubmitting}
								/>

								<Group
									grow
								>
									<TextInput
										label={
											<Group>
												<IconUser size={20} />
												<Text>First Name</Text>
											</Group>
										}
										{...form.getInputProps('firstName')}
										required
										withAsterisk={false}
										disabled={isSubmitting}
									/>

									<TextInput
										label={
											<Group>
												<Text>Last Name</Text>
											</Group>
										}
										{...form.getInputProps('lastName')}
										required
										withAsterisk={false}
										disabled={isSubmitting}
									/>
								</Group>

								<PasswordInput
									label={
										<Group>
											<IconLock size={20} />
											<Text>Password</Text>
										</Group>
									}
									{...form.getInputProps('password')}
									required
									withAsterisk={false}
									description="At least 6 characters, including uppercase, lowercase, a number, and a special character."
									disabled={isSubmitting}
								/>

								<PasswordInput
									label={
										<Group>
											<IconLock size={20} />
											<Text>Confirm Password</Text>
										</Group>
									}
									{...form.getInputProps('confirmPassword')}
									required
									withAsterisk={false}
									disabled={isSubmitting}
								/>

								<MultiSelect
									data={instruments.map((instrument) => ({
										value: instrument.name,
										label: instrument.name
									}))}
									label={
										<Group>
											<IconMusic size={20} />
											<Text>Which instruments do you want to sign up to record for?</Text>
											<Text c="dimmed" fz="sm">(select one or more)</Text>
										</Group>
									}
									{...form.getInputProps('instruments')}
									required
									withAsterisk={false}
									disabled={isSubmitting}
								/>

								{session.currentTos == null ? (
									<Loader />
								) : (
									<Stack>
										<Text>
											Please review our current Terms of Service.
										</Text>

										<TosPdfViewerModal
											opened={tosViewerOpen}
											onClose={() => setTosViewerOpen(false)}
										/>

										<Group>
											<Button variant="light" onClick={() => setTosViewerOpen(true)}>
												View Terms
											</Button>

											<Checkbox
												label="I agree to the Terms of Service"
												{...form.getInputProps('tosAgreed', { type: 'checkbox' })}
												disabled={isSubmitting || session.currentTos == null}
												required
											/>
										</Group>
									</Stack>
								)}

								<Button
									type="submit"
									radius="xl"
									size='sm'
									loading={isSubmitting}
								>
									Register
								</Button>
							</Stack>
						</form>
					)}
				</Stack>
			</Card>
		</Flex>
	);
};

export default InstrumentalistRegistrationModal;
